import React, { useEffect, useRef, useState } from 'react'
import { Sparkles } from 'lucide-react'
import { projectsApi, AutoMapSessionStatus } from '@/lib/projectsApi'
import { useToast } from '@/contexts/ToastContext'
import { Button } from '@/components/ui/Button'

interface Props {
  sessionId:    string
  // fires on terminal state (complete / failed / cancelled). parent uses it
  // to swap to the review pane with the final suggestions
  onTerminal:   (status: AutoMapSessionStatus) => void
}

// poll /status every 2s while state === 'running'. stop on any terminal
// state and emit onTerminal so the parent can transition.
//
// cancel button hits POST /cancel. orchestrator's per-row state check
// picks it up next iteration

const POLL_INTERVAL_MS = 2000

export const AutoMapProgressModal: React.FC<Props> = ({ sessionId, onTerminal }) => {
  const { error: showError } = useToast()
  const [status,    setStatus]    = useState<AutoMapSessionStatus | null>(null)
  const [cancelling, setCancelling] = useState(false)
  const onTerminalRef = useRef(onTerminal)
  onTerminalRef.current = onTerminal

  useEffect(() => {
    let stopped = false
    let timer: ReturnType<typeof setTimeout> | null = null

    const tick = async () => {
      try {
        const s = await projectsApi.getAutoMapStatus(sessionId)
        if (stopped) return
        setStatus(s)
        if (s.state !== 'running') {
          onTerminalRef.current(s)
          return
        }
        timer = setTimeout(tick, POLL_INTERVAL_MS)
      } catch (err: any) {
        if (stopped) return
        showError(err?.message ?? 'Failed to fetch auto-map status')
        // back off and retry once. orchestrator may be mid-update on the
        // in-memory state during the read
        timer = setTimeout(tick, POLL_INTERVAL_MS * 2)
      }
    }
    tick()

    return () => {
      stopped = true
      if (timer) clearTimeout(timer)
    }
  }, [sessionId, showError])

  const handleCancel = async () => {
    setCancelling(true)
    try {
      await projectsApi.cancelAutoMap(sessionId)
      // don't optimistically close. the poll tick picks up the cancelled
      // state and routes through onTerminal, so partial results still
      // reach the review pane
    } catch (err: any) {
      showError(err?.message ?? 'Cancel failed')
      setCancelling(false)
    }
  }

  const pct = status && status.total_assessments > 0
    ? Math.min(100, Math.round((status.processed_count / status.total_assessments) * 100))
    : 0

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl max-w-md w-full overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-200 dark:border-slate-700 flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-blue-500" />
          <h2 className="text-base font-semibold">Auto-mapping in progress…</h2>
        </div>

        <div className="p-5 space-y-3">
          <div className="space-y-1.5">
            <div className="flex items-center justify-between text-xs text-slate-500 dark:text-slate-400">
              <span>{status ? `${status.processed_count} / ${status.total_assessments} assessments` : 'Starting…'}</span>
              <span className="tabular-nums">{pct}%</span>
            </div>
            <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-2 overflow-hidden">
              <div className="bg-blue-500 h-2 rounded-full transition-all duration-200" style={{ width: `${pct}%` }} />
            </div>
          </div>

          {status?.current_assessment_id && (
            <div className="text-xs text-slate-500 dark:text-slate-400">
              Processing assessment <code className="text-[10px] bg-slate-100 dark:bg-slate-700 px-1 rounded">{status.current_assessment_id.slice(0, 8)}</code>…
            </div>
          )}

          <div className="grid grid-cols-2 gap-2 pt-1 text-[11px]">
            <Stat label="Tool calls"           value={status ? String(status.tool_calls_made) : '—'} />
            <Stat label="Suggestions so far"   value={status ? String(status.suggestions.length) : '—'} />
            <Stat label="Prompt + completion"  value={status ? `${status.prompt_tokens_total + status.completion_tokens_total} tok` : '—'} />
          </div>
        </div>

        <div className="flex items-center justify-end px-5 py-3 border-t border-slate-200 dark:border-slate-700">
          <Button variant="outline" size="sm" onClick={handleCancel} disabled={cancelling || (status?.state !== undefined && status.state !== 'running')}>
            {cancelling ? 'Cancelling…' : 'Cancel auto-map'}
          </Button>
        </div>
      </div>
    </div>
  )
}

const Stat: React.FC<{ label: string; value: string; highlight?: boolean }> = ({ label, value, highlight }) => (
  <div className={`rounded border px-2 py-1.5 ${
    highlight
      ? 'border-blue-300 dark:border-blue-700 bg-blue-50 dark:bg-blue-900/20'
      : 'border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/30'
  }`}>
    <div className="text-[9px] uppercase tracking-wide text-slate-400">{label}</div>
    <div className="text-[12px] font-semibold text-slate-900 dark:text-white tabular-nums">{value}</div>
  </div>
)
